import { memo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Button } from "./ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "./ui/carousel";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const VideoTestimonialsSection = memo(() => {
  const [api, setApi] = useState<CarouselApi>();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  const videoTestimonials = [
    {
      id: "d866b46f-dd41-4052-8f8c-1582db2805dc",
      name: "ADRIANO MENDES",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=d866b46f-dd41-4052-8f8c-1582db2805dc",
    },
    {
      id: "5bd0e94d-a780-43bf-9bd1-7c9fe681d15c",
      name: "EMERSON VIEIRA",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=5bd0e94d-a780-43bf-9bd1-7c9fe681d15c",
    },
    {
      id: "fee98725-6cff-495a-8ac0-25e74527acd1",
      name: "LUIZ FELIPE",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=fee98725-6cff-495a-8ac0-25e74527acd1",
    },
    {
      id: "f75bd60a-334a-40b7-9e55-f405c7e1e53a",
      name: "MAURÍCIO HENNEMANN",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=f75bd60a-334a-40b7-9e55-f405c7e1e53a",
    },
    {
      id: "28cc2f56-8b3e-47e8-8ab6-3e72aab07a33",
      name: "ALBERTO",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=28cc2f56-8b3e-47e8-8ab6-3e72aab07a33",
    },
    {
      id: "6e2fcbdf-3f1d-453c-b5ef-fc14b42295e0",
      name: "JULIANA",
      embedUrl: "https://player-vz-4779235b-eee.tv.pandavideo.com.br/embed/?v=6e2fcbdf-3f1d-453c-b5ef-fc14b42295e0",
    }
  ];

  // Load PandaVideo API and setup players
  useEffect(() => {
    // Check if script already exists
    if (!document.querySelector('script[src="https://player.pandavideo.com.br/api.v2.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://player.pandavideo.com.br/api.v2.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Setup PandaPlayer for each video
    (window as any).pandascripttag = (window as any).pandascripttag || [];
    videoTestimonials.forEach((testimonial) => {
      (window as any).pandascripttag.push(function() {
        const panda_id_player = `panda-${testimonial.id}`;
        const p = new (window as any).PandaPlayer(panda_id_player, {
          onReady() {
            // Removed pipScrollFollow to prevent video resizing effect
          }
        });
      });
    });
  }, []);

  return (
    <section id="video-testimonials" className="py-16 px-4 bg-slate-800/20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${
            headerVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Play className="w-4 h-4" />
            Depoimentos em Vídeo
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Veja o que nossos{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">
              clientes
            </span>{" "}
            falam
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Histórias reais de transformação e sucesso com nossos robôs traders
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
            <Carousel
              opts={{
                align: "start",
                loop: false,
                watchDrag: true,
                dragFree: false,
                containScroll: "trimSnaps",
              }}
              className="w-full"
              setApi={setApi}
            >
              <CarouselContent>
              {videoTestimonials.map((testimonial, index) => (
                <CarouselItem 
                  key={testimonial.id} 
                  className="pl-4 basis-[66.67%] md:basis-[50%] lg:basis-[33.33%] xl:basis-[20%]"
                >
                  <div className="group bg-slate-700/40 backdrop-blur-sm rounded-2xl p-2 md:p-4 hover:bg-slate-700/60 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50">
                    {/* Video Player */}
                    <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-slate-800 mb-2 md:mb-3">
                      <iframe
                        id={`panda-${testimonial.id}`}
                        src={testimonial.embedUrl}
                        style={{ border: "none" }}
                        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
                        allowFullScreen={true}
                        width="360"
                        height="640"
                        className="absolute top-0 left-0 w-full h-full"
                        title={`Depoimento de ${testimonial.name}`}
                      />
                      {/* Drag areas avoiding video controls */}
                      <div className="absolute top-4 left-2 w-8 bottom-20 md:hidden pointer-events-auto" />
                      <div className="absolute top-0 right-0 w-16 h-full md:hidden pointer-events-auto" />
                    </div>

                    {/* Client Name */}
                    <div className="text-center">
                      <h3 className="text-white font-semibold text-sm md:text-lg mb-1 truncate">
                        {testimonial.name}
                      </h3>
                      <div className="inline-flex items-center gap-1 bg-cyan-400/20 text-cyan-400 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                        <Play className="w-3 h-3" />
                        Cliente QuantMake
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {/* Navigation Buttons */}
            <button
              onClick={() => api?.scrollPrev()}
              aria-label="Vídeo anterior"
              className="group hidden md:flex absolute -left-4 sm:-left-6 top-1/2 -translate-y-1/2 bg-blue-400/40 backdrop-blur-md border-2 border-blue-400/60 text-blue-100 hover:bg-green-400/40 hover:border-green-400/60 hover:text-white shadow-lg shadow-blue-400/40 hover:shadow-lg hover:shadow-green-400/40 h-10 w-10 sm:h-12 sm:w-12 rounded-full items-center justify-center transition-all duration-300 z-20"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:-translate-x-1" />
            </button>
            <button
              onClick={() => api?.scrollNext()}
              aria-label="Próximo vídeo"
              className="group hidden md:flex absolute -right-4 sm:-right-6 top-1/2 -translate-y-1/2 bg-blue-400/40 backdrop-blur-md border-2 border-blue-400/60 text-blue-100 hover:bg-green-400/40 hover:border-green-400/60 hover:text-white shadow-lg shadow-blue-400/40 hover:shadow-lg hover:shadow-green-400/40 h-10 w-10 sm:h-12 sm:w-12 rounded-full items-center justify-center transition-all duration-300 z-20"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </Carousel>
        </div>

        {/* Trust Indicator */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 text-slate-400 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Depoimentos reais de clientes verificados
          </div>
        </div>
      </div>
    </section>
  );
});

VideoTestimonialsSection.displayName = "VideoTestimonialsSection";

export default VideoTestimonialsSection;